import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { ConfirmationDialog } from '@/lib/base/overlays';

import { disconnectMailchimp } from '../api/connect';

interface DisconnectConfirmationProps {
  orgId: number | string;
  isOpen: boolean;
  onClose: () => void;
  onDisconnected?: () => void;
  audienceName?: string | null;
}

const OUTCOME_CLASS =
  'flex gap-2 text-body-small text-[var(--color-text-secondary)]';

export function DisconnectConfirmation({
  orgId,
  isOpen,
  onClose,
  onDisconnected,
  audienceName,
}: DisconnectConfirmationProps) {
  const queryClient = useQueryClient();

  const { mutate, isPending, isError } = useMutation({
    mutationFn: () => disconnectMailchimp(orgId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['mailchimpStatus', orgId],
      });
      onDisconnected?.();
      onClose();
    },
  });

  const handleConfirm = useCallback(() => {
    mutate();
  }, [mutate]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const description = audienceName
    ? `Two-way sync with the “${audienceName}” audience will stop right away. Nothing is deleted.`
    : 'Two-way sync will stop right away. Nothing is deleted.';

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      variant='destructive'
      title='Disconnect Mailchimp?'
      description={description}
      confirmLabel='Disconnect'
      isLoading={isPending}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      instance='disconnect-mailchimp'
    >
      <ul className='mt-3 space-y-2'>
        <li className={OUTCOME_CLASS}>
          <span aria-hidden='true'>•</span>
          <span>Contacts already synced stay in both Ahoy and Mailchimp.</span>
        </li>
        <li className={OUTCOME_CLASS}>
          <span aria-hidden='true'>•</span>
          <span>Your field mappings are saved, so reconnecting is quick.</span>
        </li>
      </ul>
      {isError && (
        <p className='mt-3 text-body-small text-[var(--color-text-error-primary)]'>
          Couldn’t disconnect. Please try again.
        </p>
      )}
    </ConfirmationDialog>
  );
}
